import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useAppDispatch, useAppSelector } from '../store/store';
import { setLoading, setSession } from '../store/authSlice';
import { showMessage, toggleColorMode } from '../store/uiSlice';
import { forgotPassword, login, requestSignup } from '../api/services';
import { APP_NAME, APP_TAGLINE } from '../brand';

const isDemo = process.env.REACT_APP_MODE?.toLowerCase() === 'demo';

/** Sin contorno negro por defecto del OutlinedInput; hover/focus suaves. */
const pillFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 999,
    bgcolor: 'background.paper',
    pr: 0.5,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: (t: Theme) => alpha(t.palette.text.primary, 0.14),
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: 'primary.main',
  },
};

export default function Login() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const colorMode = useAppSelector((s) => s.ui.colorMode);
  const navigate = useNavigate();
  const [dni, setDni] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotDni, setForgotDni] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState<string | null>(null);
  const [forgotErr, setForgotErr] = useState<string | null>(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupUsername, setSignupUsername] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupMsg, setSignupMsg] = useState<string | null>(null);
  const [signupErr, setSignupErr] = useState<string | null>(null);
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    dispatch(setLoading(true));
    try {
      const data = await login(dni.trim(), password);
      dispatch(setSession({ user: data.user, memberships: data.memberships || [] }));
      dispatch(showMessage({ message: `Bienvenido${data.user.firstName ? `, ${data.user.firstName}` : ''}`, severity: 'success' }));
      if (data.user.isSuperAdmin) navigate('/home', { replace: true });
      else if (!data.memberships?.length) navigate('/no-tenant', { replace: true });
      else navigate('/home', { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (err as Error)?.message
        || 'Error al iniciar sesión';
      setError(msg);
    } finally {
      setBusy(false);
      dispatch(setLoading(false));
    }
  }

  function openForgot() {
    setForgotDni(dni.trim());
    setForgotMsg(null);
    setForgotErr(null);
    setForgotOpen(true);
  }

  async function submitForgot() {
    setForgotErr(null);
    setForgotMsg(null);
    const trimmed = forgotDni.trim();
    if (!trimmed) {
      setForgotErr('Ingresá tu DNI.');
      return;
    }
    setForgotBusy(true);
    try {
      const res = await forgotPassword(trimmed);
      setForgotMsg(res.message);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (err as Error)?.message
        || 'No se pudo procesar la solicitud';
      setForgotErr(msg);
    } finally {
      setForgotBusy(false);
    }
  }

  function openSignup() {
    setSignupUsername(dni.trim());
    setSignupPassword('');
    setSignupPhone('');
    setSignupEmail('');
    setSignupErr(null);
    setSignupMsg(null);
    setShowSignupPassword(false);
    setSignupOpen(true);
  }

  function validateSignup() {
    if (!signupUsername.trim()) return 'Ingresá usuario o DNI.';
    if (signupPassword.trim().length < 6) return 'La contraseña debe tener al menos 6 caracteres.';
    if (!/^\+?[0-9\s-]{8,20}$/.test(signupPhone.trim())) return 'Ingresá un teléfono válido.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupEmail.trim())) return 'Ingresá un email válido.';
    return null;
  }

  async function submitSignup() {
    setSignupErr(null);
    setSignupMsg(null);
    const validationError = validateSignup();
    if (validationError) {
      setSignupErr(validationError);
      return;
    }

    setSignupBusy(true);
    try {
      const response = await requestSignup({
        username: signupUsername.trim(),
        password: signupPassword,
        phone: signupPhone.trim(),
        email: signupEmail.trim(),
      });
      setSignupMsg(response.message);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (err as Error)?.message
        || 'No se pudo enviar la solicitud de alta';
      setSignupErr(msg);
    } finally {
      setSignupBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: (t) => (t.palette.mode === 'light' ? '#eceff1' : t.palette.background.default),
      }}
    >
      {/* Cabecera estilo app móvil */}
      <Box
        component="header"
        sx={{
          bgcolor: 'background.paper',
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          boxShadow: (t) => (t.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.45)' : '0 2px 8px rgba(0,0,0,0.06)'),
          pt: { xs: 'max(12px, env(safe-area-inset-top, 0px))', sm: 2.5 },
        }}
      >
        <Avatar
          sx={{
            width: 52,
            height: 52,
            bgcolor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[800] : t.palette.common.white),
            color: 'primary.main',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (t) => (t.palette.mode === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.08)'),
          }}
        >
          <BusinessIcon sx={{ fontSize: 30 }} />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 800, lineHeight: 1.2, color: 'text.primary' }}>
            Portal de gestión
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
            {APP_NAME}
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {' · '}
              {APP_TAGLINE}
            </Box>
          </Typography>
        </Box>
        <Tooltip title={colorMode === 'dark' ? 'Modo claro' : 'Modo oscuro'}>
          <IconButton
            size="small"
            aria-label={colorMode === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
            onClick={() => dispatch(toggleColorMode())}
            sx={{ color: 'text.secondary' }}
          >
            {colorMode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: 480,
          mx: 'auto',
          px: 2,
          py: 3,
          pb: { xs: 'max(24px, env(safe-area-inset-bottom, 0px))', sm: 4 },
        }}
      >
        {isDemo && (
          <Chip
            size="small"
            label="MODO DEMO"
            sx={{
              mb: 2,
              fontWeight: 800,
              letterSpacing: 0.4,
              bgcolor: '#f57c00',
              color: 'common.white',
              borderRadius: 999,
              height: 28,
              '& .MuiChip-label': { px: 1.5, py: 0 },
            }}
          />
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            <TextField
              label="Número de DNI"
              value={dni}
              onChange={(e) => setDni(e.target.value)}
              required
              autoFocus
              fullWidth
              size="medium"
              inputMode="numeric"
              autoComplete="username"
              sx={pillFieldSx}
              inputProps={{ maxLength: 12 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pl: 1.5, ml: 0.5 }}>
                    <AssignmentIndOutlinedIcon sx={{ color: 'action.active', fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              size="medium"
              autoComplete="current-password"
              sx={pillFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pl: 1.5, ml: 0.5 }}>
                    <LockOutlinedIcon sx={{ color: 'action.active', fontSize: 22 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      sx={{ color: 'action.active' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {isDemo && (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block', mt: -0.5, px: 0.5 }}>
                Demo: SuperAdmin 99999999 · Admin 11111111 u Operador 33333333 · clave: 123456
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={busy}
              fullWidth
              sx={{
                mt: 0.5,
                py: 1.5,
                borderRadius: 999,
                fontSize: '1.05rem',
                fontWeight: 800,
                minHeight: 52,
                textTransform: 'none',
                boxShadow: `0 6px 22px ${alpha(theme.palette.primary.main, 0.45)}`,
                '&:hover': {
                  boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.5)}`,
                },
              }}
            >
              {busy ? 'Ingresando…' : 'Ingresar'}
            </Button>
          </Stack>
        </Box>

        <MuiLink
          component="button"
          type="button"
          variant="body2"
          onClick={openForgot}
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 2.5,
            fontWeight: 600,
            color: 'primary.main',
            cursor: 'pointer',
            textDecoration: 'none',
            border: 'none',
            background: 'none',
            width: '100%',
            p: 0,
            font: 'inherit',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          ¿Olvidaste tu contraseña?
        </MuiLink>

        <MuiLink
          component="button"
          type="button"
          variant="body2"
          onClick={openSignup}
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 1,
            fontWeight: 600,
            color: 'primary.main',
            cursor: 'pointer',
            textDecoration: 'none',
            border: 'none',
            background: 'none',
            width: '100%',
            p: 0,
            font: 'inherit',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          ¿No tenés usuario? Solicitar alta
        </MuiLink>
      </Box>

      <Dialog open={forgotOpen} onClose={() => !forgotBusy && setForgotOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Recuperar contraseña</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {isDemo
                ? 'Ingresá tu DNI. En modo demo la clave se restablece a 123456 si el usuario existe.'
                : 'Ingresá tu DNI. Te indicamos cómo recuperar el acceso con tu organización.'}
            </Typography>
            {forgotErr && <Alert severity="error">{forgotErr}</Alert>}
            {forgotMsg && <Alert severity="success">{forgotMsg}</Alert>}
            <TextField
              label="DNI"
              value={forgotDni}
              onChange={(e) => setForgotDni(e.target.value)}
              fullWidth
              autoFocus
              disabled={forgotBusy}
              inputProps={{ inputMode: 'numeric', autoComplete: 'username' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setForgotOpen(false)} disabled={forgotBusy} color="inherit">
            Cerrar
          </Button>
          <Button variant="contained" onClick={() => void submitForgot()} disabled={forgotBusy}>
            {forgotBusy ? 'Enviando…' : 'Continuar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={signupOpen} onClose={() => !signupBusy && setSignupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Solicitud de alta</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Completá usuario, contraseña, teléfono y email. Te enviaremos por mail los datos para validar el alta.
            </Typography>
            {signupErr && <Alert severity="error">{signupErr}</Alert>}
            {signupMsg && <Alert severity="success">{signupMsg}</Alert>}
            <TextField
              label="Usuario / DNI"
              value={signupUsername}
              onChange={(e) => setSignupUsername(e.target.value)}
              fullWidth
              autoFocus
              disabled={signupBusy}
              inputProps={{ maxLength: 40, autoComplete: 'username' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentIndOutlinedIcon sx={{ color: 'action.active' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Contraseña"
              type={showSignupPassword ? 'text' : 'password'}
              value={signupPassword}
              onChange={(e) => setSignupPassword(e.target.value)}
              fullWidth
              disabled={signupBusy}
              inputProps={{ minLength: 6, maxLength: 64, autoComplete: 'new-password' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: 'action.active' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label={showSignupPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      onClick={() => setShowSignupPassword((v) => !v)}
                      edge="end"
                      size="small"
                    >
                      {showSignupPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Teléfono"
              value={signupPhone}
              onChange={(e) => setSignupPhone(e.target.value)}
              fullWidth
              disabled={signupBusy}
              inputProps={{ inputMode: 'tel', autoComplete: 'tel', maxLength: 20 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphoneOutlinedIcon sx={{ color: 'action.active' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              fullWidth
              disabled={signupBusy}
              inputProps={{ inputMode: 'email', autoComplete: 'email', maxLength: 120 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: 'action.active' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setSignupOpen(false)} disabled={signupBusy} color="inherit">
            Cerrar
          </Button>
          <Button variant="contained" onClick={() => void submitSignup()} disabled={signupBusy}>
            {signupBusy ? 'Enviando…' : 'Enviar solicitud'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
